import React, {useCallback, useEffect, useMemo, useReducer, useRef, useState} from 'react';
import {Link, router} from '@inertiajs/react';
import {Container} from 'react-bootstrap';
import {FlexToast, useExternalControls} from 'laravel-inertia-backend';
import HelpTooltip from '../../Components/HelpTooltip';
import OeTip from '../../Components/OrderEditor/OeTip';
import DocumentHeader from '../../Components/OrderEditor/DocumentHeader';
import CustomerNote from '../../Components/OrderEditor/CustomerNote';
import ChannelBar from '../../Components/OrderEditor/ChannelBar';
import PaymentBar from '../../Components/OrderEditor/PaymentBar';
import DiscountBar from '../../Components/OrderEditor/DiscountBar';
import ShipCostBar from '../../Components/OrderEditor/ShipCostBar';
import PrintMask from '../../Components/OrderEditor/PrintMask';
import PositionsTable from '../../Components/OrderEditor/PositionsTable';
import DiscountTicker from '../../Components/OrderEditor/DiscountTicker';
import StockBar from '../../Components/OrderEditor/StockBar';
import PdfOverlay from '../../Components/OrderEditor/PdfOverlay';
import ConfirmDialog from '../../Components/OrderEditor/ConfirmDialog';
import QuantityDialog from '../../Components/OrderEditor/QuantityDialog';
import LockBar from '../../Components/OrderEditor/LockBar';
import {WizardNav, WizardRail, WizardStepInfo} from '../../Components/OrderEditor/WizardRail';
import StepSchneiderLS from '../../Components/OrderEditor/StepSchneiderLS';
import StepKommischein from '../../Components/OrderEditor/StepKommischein';
import StepProduktion from '../../Components/OrderEditor/StepProduktion';
import StepLahrLS from '../../Components/OrderEditor/StepLahrLS';
import usePicking from '../../Components/OrderEditor/usePicking';
import useAutoReserve from '../../Components/OrderEditor/useAutoReserve';
import AutoReservePanel from '../../Components/OrderEditor/AutoReserve';
import ChangesReviewModal from '../../Components/OrderEditor/ChangesReviewModal';
import StaleBanner from '../../Components/OrderEditor/StaleBanner';
import SaveBar from '../../Components/OrderEditor/SaveBar';
import useDocMetaPoll from '../../Components/OrderEditor/useDocMetaPoll';
import useMenuOffset from '../../Components/OrderEditor/useMenuOffset';
import useTransmissionPolling from '../../hooks/useTransmissionPolling';
import {editorReducer, initialState} from '../../Components/OrderEditor/editorReducer';
import {
    cancelOrder,
    cancelShipment,
    shortfallNote,
    createShipments,
    deleteOrder,
    docRef,
    exportView,
    loadArticleDetail,
    loadDocMeta,
    loadDocs,
    loadDocument,
    loadEditorState,
    loadPrinters,
    loadProductionPlan,
    loadStock,
    saveChanges,
    saveEditorState,
    shipShipment,
    terminateInfo,
} from '../../Components/OrderEditor/api';
import {kitParentIds, posLabels} from '../../Components/OrderEditor/rows';
import {
    WH_OF_STAMM,
    articleStammOf,
    availCat,
    deviatingRows,
    freeOf,
    isExcluded,
    stammLabel,
    stammLagerOf,
} from '../../Components/OrderEditor/availability';
import {
    buildSaveChanges,
    computeChanges,
    countChanges,
    hasUnsavedEdits,
    itemOverridesOf,
} from '../../Components/OrderEditor/changes';
import {discountPlan, planByGroup as buildPlanByGroup} from '../../Components/OrderEditor/discount';
import {fmt} from '../../Components/OrderEditor/format';
import {
    WH_LAHR,
    barVisible,
    findKommDocs,
    hasProductionArticles,
    isLocked,
    orderHasProgress,
    sourceNormalization,
    splitPlan,
    tableVisible,
} from '../../Components/OrderEditor/wizard';

// Warnung, wenn ein überarbeiteter Beleg nach dem Speichern nicht wieder
// festgeschrieben werden konnte (restatus 'FEHLER', Ticket #133).
const OFFEN_GEBLIEBEN = 'ACHTUNG: Festschreiben fehlgeschlagen — der Auftrag steht noch offen in weclapp, bitte dort festschreiben!';

/**
 * Auftrags-Editor — Ausbaustufe 1 („Bearbeiten und Speichern").
 *
 * Die Seite übernimmt Aufbau und Optik des Node-Programms „Auftrags-
 * Schnellerfassung" (Webkultur), damit sich die Mitarbeiter darin
 * wiederfinden: derselbe Seitenkopf, dieselbe Beleg-Karte, dieselbe
 * Positionstabelle mit Filterzeile, Ampeln und Farb-Chips, dieselben
 * Beschriftungen. Der Rahmen (Menü, Kopfleiste) ist der des
 * Ordermanagements, das Stylesheet liegt unter `.oe-root` (order-editor.scss).
 *
 * Gearbeitet wird nach dem Grundsatz des Originals: Jede Änderung bleibt
 * zunächst hier stehen (blau markiert) und geht erst mit „Speichern" als Diff
 * nach weclapp — dort laufende Arbeit eines Kollegen bleibt damit erhalten.
 * Der Speichervorgang selbst ist ein Hintergrund-Job; der Fortschritt läuft
 * über den Statuskasten unten links.
 */
export default function Editor({order, initial, meta, weclappBaseUrl, settings, bearbeiter}) {
    /*
     | Belegart und Belegnummer, mit denen die Seite startet. Der
     | Lieferschein-Modus hängt an derselben Adresse wie der Auftrag: die Seite
     | gehört zur weclapp-Auftrags-ID, der Lieferschein kommt als Abfrage dazu
     | (`?type=shipment&number=LS1422`, ABGLEICH K1/E12). Der Server wertet sie
     | aus und liefert sie als Prop — die Seite liest nie selbst an der Adresse
     | herum.
     */
    const start = useMemo(() => ({
        type: initial && initial.type === 'shipment' ? 'shipment' : 'order',
        number: (initial && initial.number) || null,
    }), [initial && initial.type, initial && initial.number]);

    const [state, dispatch] = useReducer(
        editorReducer,
        initialState,
        (basis) => ({...basis, mode: start.type}),
    );
    const {addToast} = useExternalControls();
    const {poll} = useTransmissionPolling();

    // Referenz des Belegs für alle Endpunkte: Shop-Bestellung oder „w<id>"
    // für Aufträge ohne Shop-Bestellung.
    const ref = useMemo(() => docRef(order), [order]);

    /*
     | Adresse dieser Seite ohne Abfrage. Auftrag und Lieferschein teilen sie
     | sich; unterschieden werden sie nur über `?type=…&number=…`. Der Einstieg
     | über die Shop-Bestellung hat eine eigene Adresse, der Einstieg aus
     | weclapp die ID des Auftrags.
     */
    const seitenAdresse = useMemo(() => (
        order && order.id !== null && order.id !== undefined && order.id !== ''
            ? `/order-editor/${encodeURIComponent(order.id)}`
            : `/order-editor/weclapp/${encodeURIComponent(order.weclappOrderId)}`
    ), [order]);

    // Aktueller Zustand für die Rückwege asynchroner Abläufe (Speichern,
    // Frische-Poll). Ohne ihn arbeitete der Rückweg mit dem Stand von vorhin.
    const stateRef = useRef(state);
    stateRef.current = state;

    const canEdit = !!(meta && meta.abilities && meta.abilities.edit);

    // Breite des Seitenmenüs messen — die Speicherleiste unten links richtet
    // sich daran aus und liegt sonst über dem Menü (ABGLEICH E127).
    useMenuOffset();

    const [pdfOpen, setPdfOpen] = useState(false);
    const [printMaskOpen, setPrintMaskOpen] = useState(false);
    const [exporting, setExporting] = useState(false);
    const [confirm, setConfirm] = useState(null);
    const [qtyDialog, setQtyDialog] = useState(null);
    const [review, setReview] = useState(null);
    const stockAngefragt = useRef(null);

    const toast = useCallback((message, optionen = {}) => {
        addToast(
            <FlexToast
                title="Auftrags-Editor"
                variant={optionen.variant}
                autohide={optionen.autohide !== false}
                message={message}
            />,
        );
    }, [addToast]);

    // Arbeitsstand aus dem Ordermanagement: heute die Positionen ohne
    // Lieferung. Er wird vor bzw. neben dem Beleg geholt und in beiden
    // Reihenfolgen richtig angewandt.
    useEffect(() => {
        let abgebrochen = false;
        loadEditorState(ref)
            .then((d) => {
                if (!abgebrochen) {
                    dispatch({
                        type: 'state-loaded',
                        itemOverrides: d.itemOverrides || {},
                        checkedItemIds: d.checkedItemIds || [],
                        wizard: d.wizard || {},
                        shipEstimate: d.shipEstimate || null,
                    });
                }
            })
            .catch(() => {
                // Ohne Arbeitsstand fehlt nur die Wahl „Keine" — der Beleg
                // selbst bleibt vollständig bedienbar.
            });
        return () => {
            abgebrochen = true;
        };
    }, [ref]);

    /**
     * Denselben Beleg neu holen. Ohne Nummer liefert `/load` immer den Auftrag
     * hinter dieser Seite — im Lieferschein-Modus muss die Belegnummer deshalb
     * mit, sonst spränge jedes Nachladen zurück auf den Auftrag.
     */
    const ladeBeleg = useCallback((mode, number) => loadDocument(ref, {
        type: mode,
        number: mode === 'shipment' ? (number || null) : null,
    }), [ref]);

    // Beleg laden. Ohne Nummer liefert der Server den Auftrag, der an dieser
    // Bestellung hängt.
    useEffect(() => {
        let abgebrochen = false;
        dispatch({type: 'load-start'});
        loadDocument(ref, {type: start.type, number: start.number})
            .then((doc) => {
                if (!abgebrochen) {
                    dispatch({type: 'load-done', doc, mode: doc.docType === 'shipment' ? 'shipment' : 'order'});
                }
            })
            .catch((e) => {
                if (abgebrochen) {
                    return;
                }
                const message = e.response?.data?.error || e.response?.data?.message || e.message;
                dispatch({type: 'load-failed', message});
                addToast(
                    <FlexToast
                        icon="/images/logo_weclapp.svg"
                        title="Auftrags-Editor"
                        variant="danger"
                        autohide={false}
                        message={`Der Beleg konnte nicht geladen werden: ${message}`}
                    />,
                );
            });
        return () => {
            abgebrochen = true;
        };
    }, [ref, start.type, start.number]);

    // Bestand erst nach dem Beleg holen — er hängt an Belegart und Beleg-ID.
    const doc = state.order;
    useEffect(() => {
        if (!doc || !doc.orderId) {
            return undefined;
        }
        const schluessel = `${state.mode}:${doc.orderId}`;
        if (stockAngefragt.current === schluessel) {
            return undefined;
        }
        stockAngefragt.current = schluessel;

        let abgebrochen = false;
        dispatch({type: 'stock-start'});
        loadStock(ref, {type: state.mode, id: doc.orderId})
            .then((data) => {
                if (!abgebrochen) {
                    dispatch({type: 'stock-done', stock: data});
                }
            })
            .catch((e) => {
                if (abgebrochen) {
                    return;
                }
                dispatch({type: 'stock-failed'});
                const message = e.response?.data?.error || e.response?.data?.message || e.message;
                addToast(
                    <FlexToast
                        icon="/images/logo_weclapp.svg"
                        title="Auftrags-Editor"
                        variant="danger"
                        autohide={false}
                        message={`Der Bestand konnte nicht geladen werden: ${message}. Die Ampeln bleiben grau.`}
                    />,
                );
            });
        return () => {
            abgebrochen = true;
        };
    }, [doc && doc.orderId, state.mode, ref]);

    const rows = state.rows;
    const posLabelList = useMemo(() => posLabels(rows), [rows]);
    const kitParents = useMemo(() => kitParentIds(rows), [rows]);
    const planByGroup = useMemo(
        () => buildPlanByGroup(rows, state.stock, doc),
        [rows, state.stock, doc],
    );

    const locked = !!(state.mode === 'order' && doc && !doc.editable);
    const changeCount = useMemo(
        () => countChanges({rows, mode: state.mode, pending: state.pending, order: doc}),
        [rows, state.mode, state.pending, doc],
    );

    const onVatResult = useCallback((vat, result) => dispatch({type: 'vat-result', vat, result}), []);

    // ── Positionen ohne Lieferung sofort sichern ────────────────────────────
    // Die Wahl „Keine" ist kein Lager und hat in weclapp kein Feld; sie liegt
    // je Position im Ordermanagement. Damit ein zweiter Bearbeiter sie sofort
    // sieht, wird sie unmittelbar geschrieben — nicht erst beim Speichern.
    const overrideSchluessel = useMemo(() => stabil(itemOverridesOf(rows)), [rows]);
    const gespeicherterSchluessel = useMemo(() => stabil(state.savedOverrides), [state.savedOverrides]);

    useEffect(() => {
        // Nur am Auftrag: „Keine Lieferung" ist eine Entscheidung je
        // Auftragsposition — am Lieferschein gibt es sie nicht.
        if (!doc || !canEdit || state.loading || state.mode !== 'order'
            || overrideSchluessel === gespeicherterSchluessel) {
            return;
        }
        saveEditorState(ref, {
            type: state.mode,
            id: String(doc.orderId),
            itemOverrides: JSON.parse(overrideSchluessel),
        })
            .then((d) => dispatch({type: 'state-saved', itemOverrides: d.itemOverrides || {}}))
            .catch(() => toast(
                'Die Auswahl „Keine Lieferung" konnte nicht gesichert werden. Sie gilt in dieser Sitzung, '
                + 'ein zweiter Bearbeiter sieht sie aber noch nicht.',
                {variant: 'danger', autohide: false},
            ));
    }, [overrideSchluessel, gespeicherterSchluessel, doc, canEdit, state.loading, state.mode, ref, toast]);

    // ── Neue Positionen: Bestand, Stammlager und Preis-Vorschau nachladen ───
    useEffect(() => {
        if (!doc) {
            return;
        }
        const idx = rows.findIndex((r) => r._new && r.articleId && !r._detailStarted);
        if (idx < 0) {
            return;
        }
        const r = rows[idx];
        const key = r._key;
        dispatch({type: 'row-patch', index: idx, patch: {_detailStarted: true, _priceLoading: true}});

        loadArticleDetail(ref, r.articleId, r.quantity || '1')
            .then((d) => {
                if (d && !d.error) {
                    dispatch({type: 'article-detail', articleId: r.articleId, detail: d});
                    dispatch({type: 'row-patch', key, patch: {_priceLoading: false, _previewPrice: d.price != null ? d.price : null}});
                    return;
                }
                dispatch({type: 'row-patch', key, patch: {_priceLoading: false}});
            })
            .catch(() => dispatch({type: 'row-patch', key, patch: {_priceLoading: false}}));
    }, [rows, doc, ref]);

    // ── Master-Schalter „Liefern ab" ────────────────────────────────────────

    const onSetAllStamm = useCallback((target, sichtbar = null) => {
        const s = stateRef.current;
        if (s.mode !== 'order' || !s.order) {
            return;
        }
        const name = target === 'SNV' ? 'Schneider (SNV)' : target;
        // Filter oder Suche aktiv: nur die sichtbaren Zeilen (Ticket #124).
        if (Array.isArray(sichtbar)) {
            const gesamt = s.rows.filter((r) => !r.del && r.articleId).length;
            const zeilen = sichtbar
                .map((i) => ({r: s.rows[i], i}))
                .filter(({r}) => r && !r.del && r.articleId);
            const ausgenommen = zeilen.filter(({r}) => isExcluded(r));
            const zuSetzen = zeilen.filter(({r}) => !isExcluded(r) && stammLagerOf(r, s.stock) !== target);
            if (!zuSetzen.length) {
                toast(ausgenommen.length && ausgenommen.length === zeilen.length
                    ? `Die sichtbaren Positionen stehen auf „Keine" — der Schalter lässt sie unverändert.`
                    : `Die sichtbaren Positionen stehen bereits auf ${name}.`);
                return;
            }
            setConfirm({
                title: `Sichtbare Positionen auf „ab ${name}" stellen`,
                confirmLabel: 'Umstellen',
                body: (
                    <>
                        <p>
                            <b>{zuSetzen.length}</b> der <b>{zeilen.length}</b> sichtbaren Positionen (von {gesamt}
                            {' '}im Auftrag) auf „ab {name}" stellen?
                        </p>
                        {zeilen.length - zuSetzen.length - ausgenommen.length > 0 ? (
                            <p className="muted">
                                {zeilen.length - zuSetzen.length - ausgenommen.length} sichtbare Position(en) stehen
                                {' '}schon auf {name}.
                            </p>
                        ) : null}
                        {ausgenommen.length ? (
                            <p>
                                <b>{ausgenommen.length}</b> sichtbare Position(en) stehen auf <b>„Keine"</b> und bleiben
                                unverändert.
                            </p>
                        ) : null}
                        <p className="muted">
                            Das Auftragslager und der Filter bleiben, wie sie sind. Wirksam wird das mit
                            „Speichern" (je Position im weclapp-Feld Stammlager).
                        </p>
                    </>
                ),
                onConfirm: () => {
                    dispatch({type: 'set-stamm-many', target, indices: zuSetzen.map(({i}) => i)});
                    toast(`🏠 ${zuSetzen.length} sichtbare Position(en) ab ${name} — jetzt speichern`);
                },
            });
            return;
        }
        // Positionen, die ausdrücklich auf „Keine" stehen, bleiben stehen: Der
        // Master-Schalter ist eine Bequemlichkeit, die ausdrückliche Wahl
        // „wird nicht geliefert" ist eine Entscheidung und wiegt schwerer
        // (ABGLEICH H36). Zurücknehmen lässt sie sich mit „↺ Stammlager".
        const alle = s.rows.filter((r) => !r.del && r.articleId);
        const ausgenommen = alle.filter((r) => isExcluded(r));
        const betroffen = alle.filter((r) => !isExcluded(r));
        if (!betroffen.length) {
            toast(ausgenommen.length
                ? `Alle Positionen stehen auf „Keine" — der Schalter lässt sie unverändert.`
                : 'Es gibt keine Positionen zum Umstellen.');
            return;
        }
        const zuSetzen = betroffen.filter((r) => stammLagerOf(r, s.stock) !== target);
        const wh = WH_OF_STAMM[target];
        const lagerNoetig = (s.stock.warehouses || []).some((w) => String(w.id) === String(wh))
            && String(wh) !== String(s.order.warehouseId)
            && String(s.pending.warehouse || '') !== String(wh);

        if (!zuSetzen.length && !lagerNoetig) {
            toast(`Es steht bereits alles auf ${name}.`);
            return;
        }

        setConfirm({
            title: `Alle Positionen auf „ab ${name}" stellen`,
            confirmLabel: 'Umstellen',
            body: (
                <>
                    <p>
                        <b>{zuSetzen.length}</b> von <b>{alle.length}</b> Position(en) werden auf
                        {' '}„ab {name}" gestellt.
                    </p>
                    {ausgenommen.length ? (
                        <p>
                            <b>{ausgenommen.length}</b> Position(en) stehen auf <b>„Keine"</b> und bleiben
                            unverändert — die ausdrückliche Wahl „wird nicht geliefert" hebt der Schalter
                            nicht auf. Zurücknehmen lässt sie sich mit „↺ Stammlager".
                        </p>
                    ) : null}
                    {lagerNoetig ? (
                        <p>
                            Zusätzlich wird das <b>Auftragslager</b> auf {name} umgestellt — nur am Auftragslager
                            lässt weclapp reservieren.
                        </p>
                    ) : null}
                    <p className="muted">
                        Wirksam wird das mit „Speichern" (je Position im weclapp-Feld Stammlager).
                    </p>
                </>
            ),
            onConfirm: () => {
                dispatch({type: 'set-all-stamm', target, warehouseId: lagerNoetig ? wh : null});
                toast(`🏠 alles ab ${name} (${zuSetzen.length} Position(en) geändert) — jetzt speichern`);
            },
        });
    }, [toast]);

    const onResetStamm = useCallback(() => {
        const s = stateRef.current;
        const betroffen = deviatingRows(s.rows, s.stock);
        if (!betroffen.length) {
            toast('Alle Positionen stehen bereits auf ihrem Stammlager.');
            return;
        }
        const beispiele = betroffen.slice(0, 8);

        setConfirm({
            title: 'Positionen auf ihr Stammlager zurücksetzen',
            confirmLabel: 'Zurücksetzen',
            body: (
                <>
                    <p>
                        <b>{betroffen.length}</b> Position(en) weichen vom Stammlager ab und werden zurückgesetzt:
                    </p>
                    <ul style={{paddingLeft: 18, margin: '0 0 8px'}}>
                        {beispiele.map((r) => (
                            <li key={r.id || r._key}>
                                {r.articleNumber}: {stammLabel(stammLagerOf(r, s.stock))}
                                {' → '}{stammLabel(articleStammOf(r, s.stock))}
                            </li>
                        ))}
                    </ul>
                    {betroffen.length > 8 ? (
                        <p className="muted">… und {betroffen.length - 8} weitere</p>
                    ) : null}
                    {betroffen.some((r) => isExcluded(r)) ? (
                        <p>
                            Darunter sind Positionen auf <b>„Keine"</b> — anders als „alle Lahr"/„alle Schneider"
                            nimmt der Rücksetzer auch diese Wahl zurück; sie werden danach wieder geliefert.
                        </p>
                    ) : null}
                    <p className="muted">Wirksam wird das mit „Speichern".</p>
                </>
            ),
            onConfirm: () => {
                dispatch({type: 'reset-stamm'});
                toast(`↺ ${betroffen.length} Position(en) auf ihr echtes Stammlager zurückgesetzt`);
            },
        });
    }, [toast]);

    const onAddPosition = useCallback((article, qty) => {
        dispatch({
            type: 'add-row',
            row: {
                articleId: article.id,
                articleNumber: article.articleNumber,
                title: article.name,
                quantity: qty,
                unitPrice: null,
                discountPercentage: '0',
                availability: '',
            },
        });
    }, []);

    // ── Speichern ───────────────────────────────────────────────────────────

    /** Kopfdaten und gelieferte Mengen nachziehen, ohne Eingaben zu verlieren. */
    const refreshDocMeta = useCallback(async (highlight) => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        const id = s.order.orderId;
        const mode = s.mode;
        try {
            const frisch = await ladeBeleg(mode, s.order.number);
            const c = stateRef.current;
            if (!c.order || c.order.orderId !== id || c.mode !== mode) {
                return;
            }
            dispatch({type: 'refresh-meta', doc: frisch, highlight});
            if (highlight && highlight.length) {
                window.setTimeout(() => dispatch({type: 'clear-doclinks'}), 1600);
            }
        } catch (e) {
            // Komfort-Abgleich — ein Fehler bleibt ohne Folge.
        }
    }, [ladeBeleg]);

    /*
     | Automatisches Reservieren im Hintergrund (1.2.15): Der Server legt beim
     | Speichern von Mengen einen Lauf auf die Queue. Hier wird nur sein Stand
     | abgefragt — nach dem Lauf werden Bestand und Beleg still nachgezogen,
     | die Seite lädt nicht neu. `doSave` erreicht den Hook über die Referenz,
     | weil es vor ihm definiert ist.
     */
    const autoRef = useRef(null);
    const auto = useAutoReserve(
        ref,
        !!doc && state.mode === 'order' && !state.loading,
        () => {
            refreshStock();
            refreshDocMeta();
        },
    );
    autoRef.current = auto;

    const doSave = useCallback(async (force) => {
        const s = stateRef.current;
        const beleg = s.order;
        if (!beleg) {
            return;
        }

        const changes = buildSaveChanges({rows: s.rows, mode: s.mode, pending: s.pending, order: beleg});
        if (force) {
            changes.customerDescriptionForce = true;
        }

        // Beleg-Kontext einfrieren: Während des Speicherns darf weitergeklickt
        // werden — die Antwort muss trotzdem beim richtigen Beleg landen.
        const docId = beleg.orderId;
        const docType = s.mode;
        const webUrl = beleg.webUrl;
        const currency = beleg.currency;
        const gesendeteZeilen = new Map(s.rows.filter((r) => !r._new).map((r) => (
            [String(r.id), JSON.stringify([r.quantity, r.discountPercentage, r.del])]
        )));
        const gesendeteNeue = new Set(s.rows.filter((r) => r._new).map((r) => r._key));
        const gesendeteKundeninfo = changes.customerDescription != null ? changes.customerDescription : null;

        dispatch({type: 'save-start'});

        try {
            const antwort = await saveChanges(ref, {
                orderId: String(docId),
                id: String(docId),
                type: docType,
                changes,
            });
            dispatch({type: 'save-progress', jobId: antwort.jobId});

            const daten = await poll(antwort.jobId, {
                interval: 700,
                onProgress: (d) => {
                    if (d && d.progress) {
                        dispatch({type: 'save-progress', progress: d.progress});
                    }
                },
            });

            const j = daten.result || {};
            const cd = j.customerDescription;

            // Trockenlauf: nichts ist in weclapp passiert, also bleiben die
            // Änderungen stehen — sonst hielte der Bearbeiter sie für erledigt.
            if (j.dryRun) {
                dispatch({
                    type: 'save-status',
                    status: {
                        tone: 'warn',
                        text: 'Trockenlauf — nichts nach weclapp geschrieben. Die Änderungen bleiben stehen.',
                    },
                });
                toast('Trockenlauf — nichts nach weclapp geschrieben');
                return;
            }

            const zusatz = [];
            const offenGeblieben = j.restatus === 'FEHLER';
            if (offenGeblieben) {
                zusatz.push(OFFEN_GEBLIEBEN);
            } else if (j.reworked) {
                zusatz.push('Beleg neu erzeugt');
            }
            if (j.skipped && j.skipped.length) {
                zusatz.push(`${j.skipped.length} Änderung(en) übersprungen`);
            }
            if (cd) {
                if (cd.ok) {
                    if (cd.saved) {
                        zusatz.push('Kundeninfo aktualisiert');
                    }
                } else {
                    zusatz.push('⚠ Kundeninfo NICHT gespeichert'
                        + (cd.needsConfirm ? ' (zu viel gelöscht — bitte erneut bestätigen)' : (cd.error ? ': ' + cd.error : '')));
                }
            }

            const netto = docType === 'order' ? ` · Netto ${fmt(j.netAmount)} ${currency || ''}` : '';
            const text = `✓ ${docType === 'order' ? 'Auftrag' : 'Lieferschein'} gespeichert${netto}`
                + (zusatz.length ? ' · ' + zusatz.join(' · ') : '');

            const c = stateRef.current;
            if (c.order && c.order.orderId === docId && c.mode === docType) {
                // Eingaben, die WÄHREND des Speicherns entstanden sind, auf den
                // frischen Stand mitnehmen.
                const nachtraege = c.rows.filter((r) => (r._new
                    ? !gesendeteNeue.has(r._key)
                    : gesendeteZeilen.get(String(r.id)) !== JSON.stringify([r.quantity, r.discountPercentage, r.del])));

                dispatch({
                    type: 'save-applied',
                    result: j,
                    later: nachtraege,
                    customerDescription: (cd && cd.ok && gesendeteKundeninfo != null) ? gesendeteKundeninfo : null,
                    keepCustDesc: (cd && !cd.ok) ? gesendeteKundeninfo : null,
                });
            }

            dispatch({type: 'save-status', status: {tone: offenGeblieben ? 'err' : 'ok', text, weclappUrl: webUrl}});
            if (offenGeblieben) {
                toast(OFFEN_GEBLIEBEN, {variant: 'danger', autohide: false});
            } else {
                toast('✓ Auftrag gespeichert');
            }
            refreshDocMeta();

            // Mengenübernahme: der Server reserviert jetzt im Hintergrund.
            if (docType === 'order' && autoRef.current) {
                autoRef.current.nachSpeichern();
            }

            // Löschschutz der Kundeninfo: Der Server hat den Text bewusst nicht
            // übernommen, weil ein großer Teil verschwindet. Ausdrücklich
            // bestätigen und erneut senden.
            if (cd && cd.needsConfirm) {
                setConfirm({
                    title: 'Achtung — Kundeninfo (Kundenstamm)',
                    confirmLabel: 'Ja, so speichern',
                    tone: 'danger',
                    body: (
                        <>
                            <p>
                                Du entfernst einen großen Teil des Textes
                                {cd.removedChars ? ` (${cd.removedChars} Zeichen weniger)` : ''}.
                            </p>
                            <p>Das wirkt auf <b>alle Belege dieses Kunden</b>.</p>
                            <p>Wirklich so speichern?</p>
                        </>
                    ),
                    onConfirm: () => doSave(true),
                });
            }
        } catch (e) {
            const t = e.transmission || {};
            const meldung = (t.result && t.result.error) || t.error_message || e.response?.data?.error || e.message;
            // Auch wenn das Speichern scheiterte, kann der Beleg nach dem
            // Überarbeiten offen stehen geblieben sein (Ticket #133, AB5658)
            // — das muss der Bearbeiter sehen, sonst bleibt er liegen.
            const offen = t.result && t.result.restatus === 'FEHLER';
            const text = 'Fehler: ' + meldung + (offen ? ' · ' + OFFEN_GEBLIEBEN : '');
            dispatch({type: 'save-status', status: {tone: 'err', text, weclappUrl: offen ? webUrl : undefined}});
            toast(`Speichern fehlgeschlagen: ${meldung}${offen ? ' · ' + OFFEN_GEBLIEBEN : ''}`, {variant: 'danger', autohide: false});
        }
    }, [ref, poll, toast, refreshDocMeta]);

    const onSaveClick = useCallback(() => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        const changes = computeChanges({rows: s.rows, mode: s.mode, pending: s.pending, order: s.order});
        if (!changes.length) {
            dispatch({type: 'save-status', status: {tone: 'ok', text: 'keine Änderungen'}});
            return;
        }

        // Erinnerung: erfüllte Staffeln, die noch nicht gesetzt sind.
        const offen = (s.mode === 'order' && s.stock.groups)
            ? discountPlan(s.rows, s.stock, s.order).filter((p) => p.pct !== null && p.toChange > 0)
            : [];

        const direkt = s.mode === 'shipment' ? 'NEW' : 'ORDER_ENTRY_IN_PROGRESS';
        const rework = s.order.status !== direkt
            ? (s.mode === 'shipment'
                ? 'Lieferschein ist festgeschrieben. Er wird automatisch überarbeitet, gespeichert und wieder '
                    + 'festgeschrieben — der Beleg (inkl. Sammel-/Zoll-PDFs) wird dabei neu erzeugt.'
                : `Auftrag ist festgeschrieben (${s.order.status}). Er wird automatisch überarbeitet, gespeichert `
                    + 'und wieder festgeschrieben — die AB wird dabei neu erzeugt. Bei großen Aufträgen kann das '
                    + '1–2 Minuten dauern.')
            : null;

        setReview({
            changes,
            discountHints: offen.map((p) => ({label: p.label, pct: p.pct})),
            reworkText: rework,
        });
    }, []);

    // ── Frische-Erkennung ───────────────────────────────────────────────────

    useDocMetaPoll(!!doc && !state.save.running && !auto.laeuft, async () => {
        const s = stateRef.current;
        if (!s.order || s.save.running) {
            return;
        }
        const id = s.order.orderId;
        const mode = s.mode;
        const basis = s.meta.version;

        const j = await loadDocMeta(ref, {type: mode, id});
        const c = stateRef.current;
        if (!c.order || c.order.orderId !== id || c.mode !== mode) {
            return;
        }
        if (!j.version || String(j.version) === String(basis)) {
            return;
        }

        if (hasUnsavedEdits({rows: c.rows, mode: c.mode, pending: c.pending, order: c.order})) {
            dispatch({type: 'stale-show'});
            return;
        }

        // Ohne eigene Änderungen wird der neue Stand still nachgezogen.
        const frisch = await ladeBeleg(mode, c.order.number);
        dispatch({type: 'quiet-merge', doc: frisch});
        toast('In weclapp geändert — Ansicht nachgezogen');
    });

    const staleReload = useCallback(() => {
        dispatch({type: 'stale-hide'});
        dispatch({type: 'load-start'});
        ladeBeleg(stateRef.current.mode, stateRef.current.order && stateRef.current.order.number)
            .then((d) => dispatch({type: 'load-done', doc: d, mode: d.docType === 'shipment' ? 'shipment' : 'order'}))
            .catch((e) => dispatch({type: 'load-failed', message: e.response?.data?.error || e.message}));
    }, [ladeBeleg]);

    // Warnung beim Verlassen, solange gespeichert wird oder Änderungen offen
    // sind (Original `beforeunload`, 5316).
    useEffect(() => {
        const warnen = (e) => {
            const s = stateRef.current;
            const offen = s.save.running
                || hasUnsavedEdits({rows: s.rows, mode: s.mode, pending: s.pending, order: s.order});
            if (offen) {
                e.preventDefault();
                e.returnValue = '';
            }
        };
        window.addEventListener('beforeunload', warnen);
        return () => window.removeEventListener('beforeunload', warnen);
    }, []);

    // Excel-Export der aktuellen Ansicht: Filter und Sortierung wie angezeigt.
    const onExport = useCallback(async (sortedEntries) => {
        if (!doc) {
            return;
        }
        setExporting(true);
        const hasPrice = state.mode === 'order';
        const avTxt = ['nicht verfügbar', 'teilweise', 'verfügbar'];
        const headers = ['Pos', 'Art-Nr', 'Bezeichnung', 'Verfügbarkeit', 'Menge']
            .concat(hasPrice ? ['Einzelpreis €', 'Rabatt %'] : [])
            .concat(['Verfügbar (Lieferlager)']);
        const rowsOut = sortedEntries.map((entry, nr) => {
            const r = entry.row;
            const c = availCat(r, state.stock, doc);
            const free = state.stock.loaded ? freeOf(r, state.stock, doc) : undefined;
            return [nr + 1, r.articleNumber || '', (r.title || '') + (r.locked ? ' (Set-Inhalt)' : ''),
                c >= 0 ? avTxt[c] : '', parseFloat(r.quantity) || 0]
                .concat(hasPrice ? [parseFloat(r.unitPrice) || 0, parseFloat(r.discountPercentage) || 0] : [])
                .concat([free === undefined ? '' : free]);
        });
        const widths = [6, 12, 46, 14, 9].concat(hasPrice ? [12, 9] : []).concat([14]);
        const filename = `${doc.number}_Positionen.xlsx`;

        try {
            const blob = await exportView(ref, {filename, sheetName: doc.number, headers, rows: rowsOut, widths});
            // Herunterladen über einen kurzlebigen Verweis — die Seite bleibt stehen.
            const a = document.createElement('a');
            a.href = URL.createObjectURL(blob);
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(a.href), 5000);
            toast(`Excel-Datei „${filename}" mit ${rowsOut.length} Position(en) erzeugt.`);
        } catch (e) {
            const message = e.response?.data?.error || e.message;
            toast(`Der Excel-Export ist fehlgeschlagen: ${message}`, {variant: 'danger', autohide: false});
        } finally {
            setExporting(false);
        }
    }, [doc, ref, state.mode, state.stock, toast]);

    const onFilterGroup = useCallback((key) => {
        dispatch({type: 'set-view', patch: {groupFilter: key, stammFilter: 'all'}});
        const label = ((state.stock.rules || []).find((x) => x.key === key) || {}).label || key;
        toast(`Tabelle gefiltert auf Rabattgruppe „${label}" — mit „Filter zurücksetzen" wieder alle.`);
    }, [state.stock.rules, toast]);

    // ── Rabatte anwenden (Stufe 2) ──────────────────────────────────────────
    // Gesetzt wird nur die Tabelle; nach weclapp geht das erst mit „Speichern".

    const onApplyDiscounts = useCallback(() => {
        dispatch({type: 'discount-apply-all'});
        toast('✓ Rabatte in der Tabelle gesetzt (blau markiert) — gespeichert wird erst mit „Speichern".');
    }, [toast]);

    const onApplyDiscountGroup = useCallback((key, text) => {
        dispatch({type: 'discount-apply-group', key});
        if (text) {
            toast(text);
        }
    }, [toast]);

    /**
     * Verknüpften Beleg öffnen (Original `openDoc`, ABGLEICH E31/E32): vom
     * Auftrag auf einen seiner Lieferscheine und vom Lieferschein zurück auf
     * seinen Auftrag. Beide hängen an derselben Seite — es wechselt nur die
     * Abfrage `?type=…&number=…`. Der Wechsel läuft über Inertia, damit weder
     * die Seite neu lädt noch der Terminal-Bereich abreißt.
     */
    const openDoc = useCallback((number) => {
        const nummer = String(number || '').trim();
        if (!nummer) {
            return;
        }
        const ziel = /^ls/i.test(nummer) ? `${seitenAdresse}?type=shipment&number=${encodeURIComponent(nummer)}` : seitenAdresse;
        router.visit(ziel, {preserveScroll: false});
    }, [seitenAdresse]);

    /**
     * Lieferschein stornieren (Original `openCancelShipModal` 4772 und
     * `doCancelShipment` 4783). Danach zurück auf den Auftrag — der Beleg,
     * auf dem man stand, existiert nicht mehr.
     */
    const onCancelShipment = useCallback(() => {
        const s = stateRef.current;
        if (s.mode !== 'shipment' || !s.order) {
            return;
        }
        const beleg = s.order;
        const auftragsnummer = beleg.order && beleg.order.number;

        setConfirm({
            title: 'Lieferschein stornieren',
            confirmLabel: 'Ja, stornieren',
            tone: 'danger',
            body: (
                <>
                    <p className="weg">⚠ Lieferschein <b>{beleg.number}</b> wird gelöscht/storniert.</p>
                    <p>
                        Die Positionen werden am Auftrag{auftragsnummer ? <> <b>{auftragsnummer}</b></> : null}
                        {' '}wieder offen — du kannst dann neu (zum Beispiel ab einem anderen Lager) liefern.
                    </p>
                </>
            ),
            onConfirm: async () => {
                dispatch({type: 'save-status', status: {tone: 'work', text: 'Lieferschein wird storniert…'}});
                try {
                    const j = await cancelShipment(ref, beleg.orderId);
                    if (j && j.dryRun) {
                        dispatch({type: 'save-status', status: {tone: 'warn', text: j.message || 'Trockenlauf — nichts nach weclapp geschrieben.'}});
                        toast('Trockenlauf — der Lieferschein bleibt bestehen.');
                        return;
                    }
                    if (!j || !j.ok) {
                        const meldung = (j && j.error) || 'unbekannt';
                        dispatch({type: 'save-status', status: {tone: 'err', text: 'Storno fehlgeschlagen: ' + meldung}});
                        toast('Storno fehlgeschlagen: ' + meldung, {variant: 'danger', autohide: false});
                        return;
                    }
                    dispatch({type: 'save-status', status: {tone: 'ok', text: `✓ ${j.number || 'Lieferschein'} storniert`}});
                    toast('Lieferschein storniert — zurück zum Auftrag' + shortfallNote(j));
                    // Ein Lieferschein ohne Auftrag lässt sich im Editor gar
                    // nicht öffnen (StartController::resolve), deshalb führt
                    // der Weg immer zurück auf den Auftrag.
                    openDoc(auftragsnummer || '');
                } catch (e) {
                    const meldung = e.response?.data?.error || e.message;
                    dispatch({type: 'save-status', status: {tone: 'err', text: 'Storno fehlgeschlagen: ' + meldung}});
                    toast('Storno fehlgeschlagen: ' + meldung, {variant: 'danger', autohide: false});
                }
            },
        });
    }, [ref, toast, openDoc]);


    // ── Geführter Ablauf (Stufe 3) ──────────────────────────────────────────

    const wizard = state.wizard;
    const abilities = (meta && meta.abilities) || {};
    const lahrGesperrt = settings && settings.lahrStockTrusted === false;

    /**
     * Kontext für alle Wizard-Regeln (wizard.js). Ein Objekt statt vieler
     * Argumente: dieselbe Wahrheit für Breadcrumb, Navigation, Sichtbarkeit
     * und die Knopf-Freigaben in den Schritten.
     */
    const wizCtx = useMemo(() => ({
        mode: state.mode,
        order: doc,
        rows,
        stock: state.stock,
        pending: state.pending,
        kommWiz: wizard.kommWiz,
        progress: wizard.progress,
        prodPlan: wizard.prodPlan,
        shipEstimate: wizard.shipEstimate,
        discManual: state.view.discManual,
    }), [state.mode, doc, rows, state.stock, state.pending, wizard.kommWiz, wizard.progress, wizard.prodPlan,
        wizard.shipEstimate, state.view.discManual]);

    const step = wizard.step;
    const gesperrt = isLocked(wizCtx);
    const onStep = useCallback((n) => dispatch({type: 'wiz-step', step: n}), []);

    const ask = useCallback((dialog) => setConfirm(dialog), []);
    const askQty = useCallback((dialog) => setQtyDialog(dialog), []);

    /** Bestand nach einer Buchung frisch holen (für Produktion und Picking). */
    const refreshStock = useCallback(async () => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        try {
            const data = await loadStock(ref, {type: s.mode, id: s.order.orderId});
            dispatch({type: 'stock-refresh', stock: data});
        } catch (e) {
            toast('Der Bestand konnte nicht neu geladen werden — die Ampeln zeigen noch den alten Stand.',
                {variant: 'danger'});
        }
    }, [ref, toast]);

    const picking = usePicking({stateRef, dispatch, orderRef: ref, toast, ask, askQty});

    // Fortschritt aus den Dokumenten am Beleg ableiten (Original
    // `loadProgress`, 3331): liegt ein Kommissionierschein vor, ist Schritt 4
    // erledigt — auch wenn er außerhalb dieser Seite entstanden ist.
    useEffect(() => {
        if (!doc || !doc.orderId || state.mode !== 'order' || wizard.progress) {
            return;
        }
        let abgebrochen = false;
        loadDocs(ref, {type: 'order', id: doc.orderId})
            .then((d) => {
                if (abgebrochen) {
                    return;
                }
                const docs = d.docs || [];
                dispatch({
                    type: 'wiz-patch',
                    patch: {
                        progress: {
                            kommDoc: docs.some((x) => /kommission/i.test(x.name || '')),
                            abDoc: docs.some((x) => /auftragsbest|sammelpos/i.test(x.name || '')),
                            docs,
                        },
                    },
                });
            })
            .catch(() => {
                // Ohne die Dokumentenliste fehlt nur das Häkchen an Schritt 4.
            });
        return () => {
            abgebrochen = true;
        };
    }, [doc && doc.orderId, state.mode, wizard.progress, ref]);

    // Kommissionierscheine und Drucker erst laden, wenn Schritt 3 oder 4
    // sichtbar wird (Original `loadKommData`, 3347).
    useEffect(() => {
        if (!doc || (step !== 3 && step !== 4) || wizard.kommState !== null || wizard.kommLoading) {
            return;
        }
        dispatch({type: 'wiz-patch', patch: {kommLoading: true}});
        Promise.all([
            loadDocs(ref, {type: 'order', id: doc.orderId}).catch(() => ({})),
            abilities.warehouse ? loadPrinters(ref).catch(() => ({})) : Promise.resolve({}),
        ]).then(([d, p]) => {
            const docs = d.docs || [];
            const os = ((p.os || [])[0]) || {};
            const gefunden = findKommDocs(docs);
            dispatch({
                type: 'wiz-patch',
                patch: {
                    kommLoading: false,
                    kommState: {
                        docs,
                        komm: gefunden[0] || null,
                        kommDocs: gefunden,
                        printers: os.printers || [],
                        osName: os.osName || '',
                        preferred: p.preferred || null,
                        preferredFixed: !!p.preferredFixed,
                    },
                },
            });
        }).catch(() => dispatch({type: 'wiz-patch', patch: {kommLoading: false}}));
    }, [doc && doc.orderId, step, wizard.kommState, wizard.kommLoading, ref, abilities.warehouse]);

    /** Produktionsplan holen (Original `loadProductionPlan`, 2281). */
    const reloadProdPlan = useCallback(async (erzwingen) => {
        const s = stateRef.current;
        if (!s.order || (!erzwingen && s.wizard.prodPlanLoading)) {
            return;
        }
        dispatch({type: 'wiz-patch', patch: {prodPlanLoading: true}});
        try {
            const j = await loadProductionPlan(ref);
            if (j && j.ok) {
                dispatch({type: 'wiz-patch', patch: {prodPlan: j, prodPlanLoading: false}});
                return;
            }
            dispatch({type: 'wiz-patch', patch: {prodPlanLoading: false}});
        } catch (e) {
            dispatch({type: 'wiz-patch', patch: {prodPlanLoading: false}});
        }
    }, [ref]);

    // Produktionsplan erst in Schritt 5 (Original `renderWizard`, 3289).
    useEffect(() => {
        if (!doc || step !== 5 || wizard.prodPlan !== null || wizard.prodPlanLoading) {
            return;
        }
        if (!abilities.production || !hasProductionArticles(wizCtx)) {
            return;
        }
        reloadProdPlan(false);
    }, [doc, step, wizard.prodPlan, wizard.prodPlanLoading, abilities.production, wizCtx, reloadProdPlan]);

    // „Wohin geht die Schneider-Ware?" normieren (Original `renderWarehouse`,
    // 4130): ohne echte Wahl auf den passenden Einzel-Modus, hängengebliebene
    // Beistell-Werte korrigieren.
    const lahrPlan = useMemo(
        () => (state.stock.loaded ? splitPlan(WH_LAHR, rows, state.stock) : null),
        [rows, state.stock],
    );

    useEffect(() => {
        if (!lahrPlan) {
            return;
        }
        const norm = sourceNormalization(wizard.orderSource, lahrPlan);
        if (norm.value !== wizard.orderSource) {
            dispatch({type: 'wiz-patch', patch: {orderSource: norm.value}});
        }
    }, [lahrPlan, wizard.orderSource]);

    // Prüf-Haken und Wizard-Marker serverseitig sichern (ABGLEICH G28, K32):
    // ein zweiter Bearbeiter sieht damit denselben Stand.
    const checkedKey = useMemo(() => JSON.stringify(wizard.checked.slice().sort()), [wizard.checked]);
    const savedCheckedKey = useMemo(
        () => JSON.stringify(wizard.savedChecked.slice().sort()),
        [wizard.savedChecked],
    );
    const kommWizKey = useMemo(() => JSON.stringify(wizard.kommWiz), [wizard.kommWiz]);
    const gesicherterMarker = useRef(null);

    useEffect(() => {
        // Prüf-Haken und Ablauf-Marker gehören zum Auftrag; der
        // Lieferschein-Modus hat keinen Ablauf.
        if (!doc || !canEdit || state.loading || state.mode !== 'order') {
            return;
        }
        const markerNeu = gesicherterMarker.current !== null && gesicherterMarker.current !== kommWizKey;
        const hakenNeu = checkedKey !== savedCheckedKey;
        gesicherterMarker.current = kommWizKey;

        if (!markerNeu && !hakenNeu) {
            return;
        }

        saveEditorState(ref, {
            type: state.mode,
            id: String(doc.orderId),
            checkedItemIds: JSON.parse(checkedKey),
            wizard: JSON.parse(kommWizKey),
        })
            .then((d) => dispatch({
                type: 'state-saved-wizard',
                checkedItemIds: d.checkedItemIds || [],
                wizard: d.wizard || {},
            }))
            .catch(() => toast(
                'Prüf-Haken und Ablauf-Marker konnten nicht gesichert werden. Sie gelten in dieser Sitzung, ein '
                + 'zweiter Bearbeiter sieht sie aber noch nicht.',
                {variant: 'danger', autohide: false},
            ));
    }, [checkedKey, savedCheckedKey, kommWizKey, doc, canEdit, state.loading, state.mode, ref, toast]);

    /** Beleg nach einer Aktion frisch laden, ohne die Seite neu zu laden. */
    const reloadDocument = useCallback(async () => {
        const s = stateRef.current;
        try {
            const frisch = await ladeBeleg(s.mode, s.order && s.order.number);
            dispatch({type: 'refresh-meta', doc: frisch, highlight: []});
        } catch (e) {
            // Komfort-Abgleich — ein Fehler bleibt ohne Folge.
        }
    }, [ladeBeleg]);

    /**
     * Lieferschein(e) erzeugen (Original `doDelivery` 4479 und
     * `doDeliveryStaged` 4403). Der Lauf ist ein Hintergrund-Job; das Ergebnis
     * kommt über die Job-Abfrage.
     */
    const runDelivery = useCallback(async (modus) => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        const norm = sourceNormalization(s.wizard.orderSource, splitPlan(WH_LAHR, s.rows, s.stock));
        // Beim gestuften Ablauf ist Lahr fest das Primärlager; beim einfachen
        // folgt es der Beistell-Entscheidung.
        const primaryWh = modus === 'both' && norm.value === 'snv' ? '8034' : WH_LAHR;
        const plan = splitPlan(primaryWh, s.rows, s.stock);
        if (!plan) {
            return;
        }
        const label = modus === 'secondary' ? 'Schneider' : (modus === 'primary' ? 'Lahr' : 'Lieferschein(e)');

        dispatch({
            type: 'deliver-start',
            mode: modus,
            status: {
                tone: 'work',
                text: modus === 'both'
                    ? 'Lieferschein(e) werden erzeugt… (weclapp rendert Belege)'
                    : `${label}-Lieferschein wird erzeugt… (weclapp rendert den Beleg)`,
            },
        });

        try {
            const antwort = await createShipments(ref, {
                primaryWarehouse: primaryWh,
                secondaryItemIds: plan.secondaryItemIds,
                excludedItemIds: plan.excludedItemIds,
                cosmosItemIds: modus === 'secondary' ? [] : plan.cosmosItemIds,
                mode: modus,
                source: norm.value,
                snvVersand: modus === 'secondary' ? s.wizard.snvVersand : null,
            });

            const daten = await poll(antwort.jobId, {
                interval: 1500,
                onProgress: (d) => {
                    const p = d && d.payload && d.payload.progress;
                    if (p && p.phase) {
                        dispatch({type: 'deliver-status', running: true, status: {tone: 'work', text: `${p.phase}…`}});
                    }
                },
            });

            const j = (daten && daten.result) || {};
            if (!j.ok) {
                const teil = (j.shipments || []).length
                    ? ` (bereits erzeugt: ${(j.shipments || []).map((x) => x.number).join(', ')})`
                    : '';
                dispatch({
                    type: 'deliver-status',
                    running: false,
                    status: {tone: 'err', text: 'Fehler: ' + (j.error || 'unbekannt') + teil},
                });
                return;
            }

            dispatch({
                type: 'deliver-result',
                result: j,
                status: {
                    tone: 'ok',
                    text: modus === 'both'
                        ? `✓ ${(j.shipments || []).length} Lieferschein(e) erzeugt`
                        : `✓ ${label}-Lieferschein erzeugt: ${(j.shipments || []).map((x) => x.number).join(', ')}`,
                },
            });

            // Beleg-PDFs und Schneider-Status neu suchen.
            dispatch({
                type: 'wiz-patch',
                patch: {
                    shipDocs: {id: null, docs: null, loading: false},
                    snvInfo: {id: null, data: null, loading: false},
                },
            });

            // Festschreiben und das Pflichtfeld „SNV Versand" dürfen nicht
            // still untergehen (Original 4425).
            const finFail = (j.shipments || []).find((x) => x.finalized && x.finalized.ok === false);
            if (finFail) {
                toast(`⚠ ${finFail.number} erzeugt, aber nicht festgeschrieben — ohne das legt weclapp kein PDF an. `
                    + `(${(finFail.finalized && finFail.finalized.error) || ''})`, {variant: 'danger', autohide: false});
            }
            const finDry = (j.shipments || []).find((x) => x.finalized && x.finalized.dryRun);
            if (finDry) {
                toast(`Trockenlauf: ${(finDry.finalized && finDry.finalized.message)
                    || 'Der Lieferschein wurde nicht festgeschrieben.'}`);
            }
            const svFail = (j.shipments || []).find((x) => x.snv && !x.snv.ok);
            if (svFail) {
                toast(`⚠ Lieferschein ${svFail.number} erzeugt, aber „SNV Versand" konnte nicht gesetzt werden — `
                    + `bitte in weclapp nachtragen. (${(svFail.snv && svFail.snv.error) || ''})`,
                {variant: 'danger', autohide: false});
            } else {
                const svOk = (j.shipments || []).find((x) => x.snv && x.snv.ok);
                if (svOk) {
                    toast(`📦 „${svOk.snv.label}" am Lieferschein ${svOk.number} eingetragen.`);
                }
            }

            if (modus === 'secondary') {
                dispatch({type: 'wiz-komm', patch: {schneiderDone: true}});
            }

            refreshDocMeta((j.shipments || []).map((x) => x.number));

            // Die frisch erzeugten Lieferschein-PDFs nachladen (weclapp
            // braucht kurz zum Rendern) — ohne Seiten-Reload.
            window.setTimeout(async () => {
                try {
                    const d = await loadDocs(ref, {type: 'order', id: s.order.orderId});
                    const nums = (j.shipments || []).map((x) => String(x.number));
                    const docs = (d.docs || []).filter((x) => nums.some((n) => (x.name || '').includes(n)));
                    if (docs.length) {
                        dispatch({type: 'deliver-docs', docs});
                    }
                } catch (e) {
                    // PDFs sind Beiwerk.
                }
            }, 2500);

            window.setTimeout(() => reloadDocument(), 1500);
        } catch (e) {
            const t = e.transmission || {};
            const meldung = (t.result && t.result.error) || t.error_message || e.response?.data?.error || e.message;
            dispatch({type: 'deliver-status', running: false, status: {tone: 'err', text: 'Fehler: ' + meldung}});
        }
    }, [ref, poll, toast, reloadDocument]);

    /**
     * Versandkosten-Schätzung merken bzw. in Schritt 8 bestätigen (Ticket
     * #119). `null` verwirft sie.
     */
    const onShipEstimate = useCallback(async (payload) => {
        const s = stateRef.current;
        if (!s.order) {
            return false;
        }
        try {
            const d = await saveEditorState(ref, {type: s.mode, id: String(s.order.orderId), shipEstimate: payload});
            dispatch({
                type: 'state-saved-wizard',
                checkedItemIds: d.checkedItemIds || [],
                wizard: d.wizard || {},
                shipEstimate: d.shipEstimate || null,
            });
            return true;
        } catch (e) {
            toast('Versandkosten-Schätzung nicht gesichert: ' + (e.response?.data?.message || e.message),
                {variant: 'danger', autohide: false});
            return false;
        }
    }, [ref, toast]);

    const deliverApi = useMemo(() => ({
        run: runDelivery,
        shipShipment: (id) => shipShipment(ref, id),
        cancelShipment: (id) => cancelShipment(ref, id),
    }), [runDelivery, ref]);

    /**
     * Auftrag löschen oder stornieren (Original `openTerminateModal` 4578,
     * `doTerminate` 4620). Was möglich ist, entscheidet frisch der Server.
     */
    const onTerminate = useCallback(async () => {
        const s = stateRef.current;
        if (!s.order) {
            return;
        }
        let info;
        try {
            info = await terminateInfo(ref, s.order.orderId);
        } catch (e) {
            info = {ok: false, error: e.response?.data?.error || e.message};
        }

        if (!info || !info.ok) {
            toast('Prüfung fehlgeschlagen: ' + ((info && info.error) || 'unbekannt'),
                {variant: 'danger', autohide: false});
            return;
        }
        if (info.locked) {
            toast(`Auftrag ist bereits „${info.status}" — hier gibt es nichts zu tun.`);
            reloadDocument();
            return;
        }

        const gruende = [];
        if ((info.shipments || []).length) {
            gruende.push(`${info.shipments.length} Lieferschein(e): ${info.shipments.map((x) => x.number).join(', ')}`);
        }
        if (info.hasInvoice) {
            gruende.push('Rechnung vorhanden');
        }
        if (info.hasKomm) {
            gruende.push('Kommissionierschein erzeugt');
        }

        const starteAktion = async (art) => {
            toast(art === 'delete' ? 'Auftrag wird gelöscht…' : 'Auftrag wird storniert…');
            try {
                const j = art === 'delete' ? await deleteOrder(ref) : await cancelOrder(ref);
                if (j.dryRun) {
                    toast(`Trockenlauf: ${j.message || 'Am Auftrag wurde nichts geändert.'}`);
                    return;
                }
                if (!j.ok) {
                    toast((art === 'delete' ? 'Löschen' : 'Storno') + ' fehlgeschlagen: ' + (j.error || 'unbekannt'),
                        {variant: 'danger', autohide: false});
                    return;
                }
                if (art === 'delete') {
                    toast(`✓ Auftrag ${j.number || s.order.number} gelöscht — er existiert in weclapp nicht mehr`,
                        {autohide: false});
                    return;
                }
                const lbl = j.status === 'CANCELLED' ? 'storniert' : 'manuell geschlossen (Rückstand verfällt)';
                const rem = (j.removedShipments || []).length
                    ? ` · gelöschte Lieferscheine: ${j.removedShipments.join(', ')}`
                    : '';
                const frei = (j.releasedReservations || []).length
                    ? ` · Reservierungen freigegeben: ${j.releasedReservations.join(', ')}`
                    : '';
                const freiFehler = j.releaseError ? ` · ⚠ ${j.releaseError}` : '';
                toast(`✓ Auftrag ${j.number || s.order.number} ${lbl}${rem}${frei}${freiFehler}`, {autohide: false});
                reloadDocument();
            } catch (e) {
                toast('Fehlgeschlagen: ' + (e.response?.data?.error || e.message),
                    {variant: 'danger', autohide: false});
            }
        };

        if (info.deletable) {
            setConfirm({
                title: 'Auftrag löschen',
                confirmLabel: 'Ja, endgültig löschen',
                tone: 'danger',
                body: (
                    <>
                        <p className="weg">⚠ Auftrag <b>{info.number}</b> wird ENDGÜLTIG GELÖSCHT.</p>
                        <p>
                            Es existieren keine Belege — der Auftrag kann komplett aus weclapp entfernt werden.
                            {' '}<b>Das lässt sich nicht rückgängig machen.</b>
                        </p>
                        <p className="muted">Keine Lieferscheine, keine Rechnung, kein Kommissionierschein.</p>
                    </>
                ),
                onConfirm: () => starteAktion('delete'),
            });
            return;
        }

        const wirdStorniert = (info.dispatchedShipments || []).length === 0;
        setConfirm({
            title: 'Auftrag stornieren',
            confirmLabel: 'Ja, stornieren',
            tone: 'danger',
            body: (
                <>
                    <p className="weg">⚠ Auftrag <b>{info.number}</b> wird STORNIERT.</p>
                    <p>
                        Löschen ist nicht mehr möglich ({gruende.join(' · ')}).{' '}
                        {wirdStorniert
                            ? <>Es wurde noch nichts versandt — der Auftrag wird auf <b>„storniert" (CANCELLED)</b>
                                {' '}gesetzt.</>
                            : <>Es wurde bereits versandt — der Auftrag wird <b>manuell geschlossen</b> (bereits
                                {' '}Geliefertes bleibt, offener Rückstand verfällt).</>}
                    </p>
                    {(info.openShipments || []).length ? (
                        <p>
                            Offene, noch nicht versandte Lieferscheine werden dabei gelöscht:{' '}
                            <b>{(info.openShipments || []).join(', ')}</b>.
                        </p>
                    ) : null}
                </>
            ),
            onConfirm: () => starteAktion('cancel'),
        });
    }, [ref, toast, reloadDocument]);

    const modeHint = settings.writeMode === 'dry-run'
        ? 'Trockenlauf — es wird nichts nach weclapp geschrieben.'
        : (settings.writeMode === 'test-orders' ? 'Testmodus — nur Testaufträge.' : null);

    return (
        <Container fluid className="pt-2">
            <div className="oe-root">
                <main className="oe-main">
                    {/* Kein eigener Seitenkopf: Logo, Benutzer und Menü stellt der
                        Rahmen des Ordermanagements. Belegnummer, Kunde und der
                        weclapp-Link stehen in der Beleg-Karte darunter; hier nur
                        Rücksprung und Shop-Bestellnummer. */}
                    <div className="oe-topline">
                        {order.id ? (
                            <OeTip tip="Zurück zur Bestellung im Ordermanagement — die Zeile wird dort aufgeklappt.">
                                <Link className="wl" href={`/order?extradata=${encodeURIComponent(order.id)}`}>
                                    ← zurück zur Bestellung
                                </Link>
                            </OeTip>
                        ) : (
                            <OeTip tip="Zurück zur Startseite des Auftrags-Editors mit Belegsuche und den zuletzt bearbeiteten Belegen.">
                                <Link className="wl" href="/order-editor">← Auftrags-Editor</Link>
                            </OeTip>
                        )}
                        {order.shopOrderNumber ? (
                            <span>
                                <span className="muted">Shop-Bestellnummer</span> <b>{order.shopOrderNumber}</b>
                            </span>
                        ) : null}
                        <HelpTooltip id="oe-topline-help" placement="bottom" size={0.75}>
                            {order.shopOrderNumber
                                ? 'Diese Seite gehört zur Bestellung aus dem Shop und zeigt den daraus entstandenen weclapp-Auftrag. '
                                : 'Diese Seite zeigt einen Beleg direkt aus weclapp. '}
                            Änderungen bleiben zunächst hier stehen und gehen erst mit „Speichern" nach weclapp.
                        </HelpTooltip>
                    </div>

                    {modeHint ? (
                        <div className="oe-modebar">
                            <span>⚠</span>
                            <span>{modeHint}</span>
                            <HelpTooltip id="oe-modebar-help" placement="bottom" size={0.75}>
                                Die Betriebsart steht in den Systemeinstellungen. Im Trockenlauf werden
                                Schreibvorgänge nur protokolliert, im Testmodus nur an Testaufträgen zugelassen.
                            </HelpTooltip>
                        </div>
                    ) : null}

                    <StaleBanner
                        show={state.stale}
                        onReload={staleReload}
                        onDismiss={() => dispatch({type: 'stale-hide'})}
                    />

                    {state.loading ? (
                        <div className="oe-state">Beleg wird geladen…</div>
                    ) : null}

                    {state.loadError ? (
                        <div className="oe-state err">
                            Der Beleg konnte nicht geladen werden: {state.loadError}
                        </div>
                    ) : null}

                    {doc && !state.loading ? (
                        <>
                            <DocumentHeader
                                mode={state.mode}
                                order={doc}
                                weclappBaseUrl={weclappBaseUrl}
                                vatResults={state.vatResults}
                                onVatResult={onVatResult}
                                onOpenPdf={() => setPdfOpen(true)}
                                onOpenPrintMask={abilities.warehouse ? () => setPrintMaskOpen(true) : null}
                                onOpenDoc={openDoc}
                                newDocLinks={state.newDocLinks}
                                canTerminate={!!abilities.ship}
                                terminateLabel={orderHasProgress(wizCtx) ? '⛔ Auftrag stornieren' : '🗑 Auftrag löschen'}
                                onTerminate={onTerminate}
                                onCancelShipment={abilities.ship ? onCancelShipment : null}
                            />

                            <CustomerNote
                                mode={state.mode}
                                order={doc}
                                pending={state.pending.custDesc}
                                editable={canEdit && !locked}
                                onChange={(value) => dispatch({type: 'set-pending', patch: {custDesc: value}})}
                            />

                            {gesperrt ? <LockBar ctx={wizCtx}/> : null}

                            {!canEdit ? (
                                <div className="lockbar">
                                    <span className="lockico">🔒</span>
                                    <span className="lockmain">
                                        <span className="locktitle">Nur Leserecht</span>
                                        <span className="lockdesc">
                                            Zum Ändern von Mengen, Rabatten und der Lagerwahl fehlt das Recht
                                            „Auftrags-Editor bearbeiten". Ansehen, Belege öffnen und der
                                            Excel-Export gehen weiterhin.
                                        </span>
                                    </span>
                                </div>
                            ) : null}

                            {/* Der geführte Ablauf (Stufe 3). Bei gesperrtem
                                Auftrag bleibt alles aus — dort gibt es nichts
                                zu tun (Original `renderLocked`, ABGLEICH E42). */}
                            {!gesperrt && state.mode === 'order' ? (
                                <>
                                    <WizardRail ctx={wizCtx} step={step} onStep={onStep}/>
                                    <WizardStepInfo step={step}/>
                                </>
                            ) : null}

                            {!gesperrt && barVisible('chbar', step) ? (
                                <ChannelBar mode={state.mode} order={doc} pendingChannel={state.pending.channel}/>
                            ) : null}

                            {!gesperrt && barVisible('paybar', step) ? (
                                <PaymentBar
                                    mode={state.mode}
                                    order={doc}
                                    pending={state.pending}
                                    canEdit={canEdit && !locked}
                                    dispatch={dispatch}
                                />
                            ) : null}

                            {!gesperrt && barVisible('whbar', step) && step === 3 ? (
                                <StepSchneiderLS
                                    ctx={wizCtx}
                                    wizard={wizard}
                                    deliver={state.deliver}
                                    orderRef={ref}
                                    dispatch={dispatch}
                                    toast={toast}
                                    ask={ask}
                                    abilities={abilities}
                                    onStep={onStep}
                                    onDeliver={deliverApi}
                                    onReload={reloadDocument}
                                />
                            ) : null}

                            {!gesperrt && step === 4 ? (
                                <StepKommischein
                                    ctx={wizCtx}
                                    wizard={wizard}
                                    orderRef={ref}
                                    dispatch={dispatch}
                                    toast={toast}
                                    abilities={abilities}
                                    poll={poll}
                                    onRefreshDocMeta={refreshDocMeta}
                                />
                            ) : null}

                            {!gesperrt && barVisible('prodbar', step) ? (
                                <StepProduktion
                                    ctx={wizCtx}
                                    wizard={wizard}
                                    orderRef={ref}
                                    toast={toast}
                                    ask={ask}
                                    askQty={askQty}
                                    abilities={abilities}
                                    settings={settings}
                                    onReloadProdPlan={reloadProdPlan}
                                    onRefreshStock={refreshStock}
                                />
                            ) : null}

                            {!gesperrt && barVisible('discbar', step) ? (
                                <DiscountBar
                                    mode={state.mode}
                                    order={doc}
                                    rows={rows}
                                    stock={state.stock}
                                    pending={state.pending}
                                    canEdit={canEdit && !locked}
                                    locked={locked}
                                    discManual={state.view.discManual}
                                    onDiscManual={(v) => dispatch({type: 'set-view', patch: {discManual: v}})}
                                    onApplyAll={onApplyDiscounts}
                                    onApplyGroup={onApplyDiscountGroup}
                                    onShowGroup={onFilterGroup}
                                    onToast={toast}
                                />
                            ) : null}

                            {!gesperrt && barVisible('shipcostbar', step) ? (
                                <ShipCostBar
                                    step={step}
                                    estimate={wizard.shipEstimate}
                                    onEstimate={onShipEstimate}
                                    mode={state.mode}
                                    order={doc}
                                    rows={rows}
                                    stock={state.stock}
                                    pending={state.pending}
                                    canEdit={canEdit && !locked}
                                    orderRef={ref}
                                    dispatch={dispatch}
                                    onToast={toast}
                                />
                            ) : null}

                            {!gesperrt && barVisible('stockbar', step) ? (
                                state.stockLoading ? (
                                    <div className="fbar" style={{display: 'flex'}}>
                                        <span className="muted">
                                            Bestand wird aus weclapp geladen — die Ampeln bleiben so lange grau.
                                        </span>
                                    </div>
                                ) : (
                                    <StockBar
                                        rows={rows}
                                        stock={state.stock}
                                        order={doc}
                                        mode={state.mode}
                                        step={step}
                                        checked={wizard.checked}
                                        lahrGesperrt={lahrGesperrt}
                                        canWarehouse={!!abilities.warehouse}
                                        onBatchPick={picking.batchPick}
                                    />
                                )
                            ) : null}

                            {!gesperrt && barVisible('whbar', step) && step === 9 ? (
                                <StepLahrLS
                                    ctx={wizCtx}
                                    wizard={wizard}
                                    deliver={state.deliver}
                                    orderRef={ref}
                                    dispatch={dispatch}
                                    toast={toast}
                                    ask={ask}
                                    abilities={abilities}
                                    onStep={onStep}
                                    onDeliver={deliverApi}
                                    onRefreshDocMeta={refreshDocMeta}
                                />
                            ) : null}

                            {!gesperrt && state.mode === 'order' ? (
                                <AutoReservePanel auto={auto} canWarehouse={!!abilities.warehouse} toast={toast}/>
                            ) : null}

                            {!gesperrt && tableVisible(step, state.mode, state.view.discManual) ? (
                                <>
                                    <DiscountTicker
                                        mode={state.mode}
                                        rows={rows}
                                        stock={state.stock}
                                        order={doc}
                                        locked={locked}
                                        onFilterGroup={onFilterGroup}
                                    />

                                    <PositionsTable
                                        mode={state.mode}
                                        order={doc}
                                        rows={rows}
                                        posLabelList={posLabelList}
                                        kitParents={kitParents}
                                        stock={state.stock}
                                        view={state.view}
                                        planByGroup={planByGroup}
                                        dispatch={dispatch}
                                        onExport={onExport}
                                        exporting={exporting}
                                        canEdit={canEdit && !locked}
                                        onSetAllStamm={onSetAllStamm}
                                        onResetStamm={onResetStamm}
                                        onAddPosition={onAddPosition}
                                        onToast={toast}
                                        step={step}
                                        checked={wizard.checked}
                                        pendingWarehouse={state.pending.warehouse}
                                        lahrGesperrt={lahrGesperrt}
                                        canWarehouse={!!abilities.warehouse}
                                        onBook={picking.buchen}
                                        onUnpick={picking.reservierungLoesen}
                                        onPosOut={picking.askPosOut}
                                        onToggleCheck={picking.toggleCheck}
                                        onMass={picking.massAvail}
                                        onCheckAllLahr={picking.checkAllLahr}
                                        orderRef={ref}
                                        autoReserve={auto}
                                    />
                                </>
                            ) : null}

                            {!gesperrt && state.mode === 'order' ? (
                                <WizardNav ctx={wizCtx} step={step} onStep={onStep}/>
                            ) : null}

                        </>
                    ) : null}
                </main>

                <PrintMask
                    show={printMaskOpen && !!doc}
                    onClose={() => setPrintMaskOpen(false)}
                    orderRef={ref}
                    order={doc}
                    rows={rows}
                    stock={state.stock}
                    canPrint={!!abilities.warehouse}
                    onToast={toast}
                />

                <PdfOverlay
                    show={pdfOpen && !!doc}
                    orderId={ref}
                    source={doc ? {type: state.mode, id: doc.orderId} : null}
                    onClose={() => setPdfOpen(false)}
                />

                {/* Die Speicherleiste schwebt unten links und steht im
                    Editor-Rahmen: ihr Stylesheet liegt unter `.oe-root`. */}
                <SaveBar
                    count={changeCount}
                    save={state.save}
                    locked={locked || !canEdit}
                    onSave={onSaveClick}
                />

                <ChangesReviewModal
                    show={!!review}
                    changes={review ? review.changes : []}
                    discountHints={review ? review.discountHints : []}
                    reworkText={review ? review.reworkText : null}
                    busy={state.save.running}
                    onCancel={() => setReview(null)}
                    onConfirm={() => {
                        setReview(null);
                        doSave(false);
                    }}
                />
            </div>

            {/* Rückfragen statt browsereigener Dialoge: Master-Schalter der
                Lagerwahl, Rücksetzer und der Löschschutz der Kundeninfo. */}
            <ConfirmDialog
                show={!!confirm}
                title={confirm ? confirm.title : ''}
                confirmLabel={confirm ? confirm.confirmLabel : undefined}
                tone={confirm ? confirm.tone : undefined}
                onConfirm={() => {
                    if (confirm && confirm.onConfirm) {
                        confirm.onConfirm();
                    }
                    setConfirm(null);
                }}
                onCancel={() => setConfirm(null)}
            >
                {confirm ? confirm.body : null}
            </ConfirmDialog>

            {/* Rückfragen mit Mengeneingabe: Bestandskorrektur beim Abhaken
                und der gezählte Ist-Bestand fertiger Sets. */}
            <QuantityDialog
                dialog={qtyDialog}
                onConfirm={(menge) => {
                    const d = qtyDialog;
                    setQtyDialog(null);
                    if (d && d.onConfirm) {
                        d.onConfirm(menge);
                    }
                }}
                onCancel={() => setQtyDialog(null)}
            />
        </Container>
    );
}

/**
 * Zuordnung mit sortierten Schlüsseln als Zeichenkette — nur so lässt sich
 * zuverlässig vergleichen, ob sich am Arbeitsstand wirklich etwas geändert
 * hat (die Reihenfolge der Schlüssel ist sonst zufällig).
 */
function stabil(obj) {
    const o = obj || {};
    const out = {};
    Object.keys(o).sort().forEach((k) => {
        out[k] = o[k];
    });
    return JSON.stringify(out);
}
